import {
  ALL_TABLES,
  AppContext,
  COMMON_CONSTANTS,
  EventEmitter,
  fetchValues,
  Generator,
  getAllColumnWidth,
  getAsyncOptions,
  getColumnWidth,
  getDateOperators,
  getTranslatedText,
  TableContainer,
  toast,
  useProduct,
  SuperList,
  useSuperListHook
} from '@nile/components';
import {
  DELETE_TASK,
  GET_TASK_LIST,
  TicketClient,
  UPDATE_TASK
} from '@nile/graphql';
import {
  DateTimeCell,
  HTMLCell,
  NameCell,
  NewInlinePriorityCell,
  SourceCell,
  TagCell,
  TechnicianCell,
  TextCell,
  TimerCell
} from '@nile/mangifera/src/Components/Task';
import { defaultColumns } from '@nile/mangifera/src/Constants/Tickets';
import { TicketContext } from '@nile/mangifera/src/Contexts';
import { getDynamicQuery } from '@nile/mangifera/src/Pebbles/TaskToolbar';
import {
  checkCustomAttribute,
  checkDynamicQuery,
  defaultColumnOrder,
  getAccessor,
  getAttributeName,
  getDefaultValue,
  getVariables as getTaskVariables,
  getType,
  isEditable,
  isFilterable,
  isSortable
} from '@nile/mangifera/src/Pebbles/TaskViewContainer';
import { constructTaskObject } from '@nile/projects/src/Utils';
import gql from 'graphql-tag';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { cloneDeep } from 'lodash';
import React, {
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState
} from 'react';
import { injectIntl } from 'react-intl';
import { withRouter } from 'react-router-dom';
import { NewTaskModal } from '../../Components/Task/HTMLCell';
import { getAllTicketFields } from '../../Constants/Tickets';

function TaskViewContainer(props) {
  const { intl } = props;
  const flags = useFlags();
  const paginationFlag = flags?.dynamicPaginationTasks;
  const context = useContext(TicketContext);
  const isMSPProduct = useProduct(COMMON_CONSTANTS.MSP);
  const appContext = useContext(AppContext);
  const params = new URLSearchParams(props.location.search);
  const taskId = params?.get('taskId');
  const module = params?.get('module');
  const tableName = ALL_TABLES.tasks;

  const columnWidthObject = getAllColumnWidth(tableName);
  const [data, setData] = useState([]);
  const dataRef = React.useRef(1);
  dataRef.current = data;
  const [columnGroup, setColumnGroup] = useState(null);
  const [editCellId, setEditCellId] = useState(null);

  const setEditCell = (cellId) => {
    setEditCellId(cellId || null);
  };
  const {
    ticketViews,
    taskViews,
    taskViewMeta,
    allEntities,
    entityPropertiesLoading
  } = props;

  const taskViewsRef = useRef();
  const matchParamsIdRef = useRef();
  taskViewsRef.current = taskViews;
  matchParamsIdRef.current = props.match?.params?.id;

  const activeView = taskViews.find((item) => {
    return item.id === props.match.params.id;
  });

  const {
    tableCondition,
    updateTableFilterCondition,
    advanceFilterCondition
  } = useSuperListHook({
    viewId: activeView?.id,
    tableName
  });

  const taskEntity = allEntities.find((el) => el.name === 'Task');
  const columns = taskViewMeta.selectAttributes
    .map((el) => {
      return taskEntity.attributes.find((attr) => attr.name === el);
    })
    .map((el) => {
      return {
        ...el,
        label: el?.label ? getTranslatedText(intl, el?.label) : el?.name || null
      };
    });

  const { sortAttributes } = taskViewMeta;

  defaultColumns.subject.actions = {
    onClick: context.openTicket
  };

  useEffect(() => {
    if (!columnGroup) {
      generateColumns();
    }
  }, []);

  const newColumns = [
    {
      name: 'source',
      label: getTranslatedText(intl, 'task.listView.header.source'),
      attributeName: 'workItem.subject',
      filterType: 'text',
      dataType: 'string'
    },
    {
      name: 'workItem.site',
      attributeName: 'workItem.site.name',
      label: getTranslatedText(intl, 'app.common.site'),
      filterType: 'text',
      dataType: 'string'
    }
  ];
  if (isMSPProduct) {
    newColumns.splice(1, 0, {
      name: 'workItem.client',
      attributeName: 'workItem.client.name',
      label: getTranslatedText(intl, 'common.client'),
      dataType: 'entity',
      entity: 'client',
      filterType: 'select'
    });
  }

  const [columnsLoading, setColumnsLoading] = useState(true);

  const generateColumns = () => {
    setColumnsLoading(true);
    const allCols = columns.concat(newColumns);
    const personalizedColumns = [];
    defaultColumnOrder.forEach((el) => {
      const currentCol = allCols.find((col) => col?.name === el);
      if (currentCol) {
        personalizedColumns.push(currentCol);
      }
    });

    const cols = personalizedColumns;
    const allColumns =
      cols?.length &&
      cols.map((col) => {
        return {
          Header: col.label,
          header: col.label,
          accessor: getAccessor(col),
          accessorKey: getAccessor(col),
          type: getType(col),
          disableSortBy: !isSortable(col, sortAttributes),
          disableFilters: !isFilterable(col),
          disableResizing: false,
          className: 'sticky',
          editable: isEditable(col),
          actions: getActions(col),
          size: getColumnWidth(columnWidthObject, col.name),
          setEditCell: setEditCell,
          field: {
            ...col,
            autoComplete: col.name === 'technician' ? true : col.autoComplete
          },
          helpers: {
            getDefaultValue,
            updateConditions: updateTableFilterCondition,
            checkDynamicQuery,
            getAsyncOptions,
            fetchValues,
            getDateOperators,
            getVariables: getTaskVariables,
            getDynamicQuery,
            entity: 'Task',
            checkCustomAttribute
          }
        };
        // columnGroup[0].columns.push(column);
      });
    setColumnGroup(allColumns);
    setColumnsLoading(false);
  };

  const save = (updatedProperties, ticket, oldData) => {
    const diff = { ...updatedProperties };
    if (ticket.ticketId) {
      diff.id = ticket.ticketId;
    }
    const generator = new Generator({
      operation: 'updateTicket',
      variables: getRequiredVariables(diff),
      fields: getAllTicketFields()
    });
    const mutationString = generator.mutationBuilder();

    TicketClient.mutate({
      mutation: gql`
        ${mutationString.query}
      `,
      variables: diff
    }).then((res) => {
      if (res?.data?.updateTicket) {
        updateMyData(res?.data?.updateTicket, diff, oldData);
        toast('success', 'Ticket updated successfully');
      } else {
        // toast('error', 'Something went wrong');
      }
    });
  };

  const updateTask = (updatedTask, taskId, args, callback) => {
    const taskObj = constructTaskObject(updatedTask);
    TicketClient.mutate({
      mutation: UPDATE_TASK,
      variables: {
        taskId: taskId,
        module: args.ticket ? 'TICKET' : 'PROJECT',
        input: taskObj
      }
    })
      .then((res) => {
        if (res?.data?.updateTask) {
          updateMyData(res.data.updateTask);
          toast('success', 'Task updated successfully');
          callback && callback();
        }
      })
      .catch((err) => {
        // toast('error', 'Something went wrong');
        callback && callback();
      });
  };

  const deleteTask = (taskId, args, callback) => {
    TicketClient.mutate({
      mutation: DELETE_TASK,
      variables: {
        taskId: taskId,
        module: args.ticket ? 'TICKET' : 'PROJECT'
      }
    })
      .then((res) => {
        if (res?.data?.deleteTask) {
          generateColumns();
          toast(
            'success',
            getTranslatedText(intl, 'ticket.details.task.delete.success')
          );
          callback && callback();
        }
      })
      .catch((err) => {
        // toast('error', 'Something went wrong');
        callback && callback();
      });
  };

  const getActions = useCallback((col) => {
    switch (col.name) {
      case 'title':
      case 'priority':
        return {
          updateTask: updateTask,
          deleteTask: deleteTask
        };

      default:
        return {
          save: save
        };
    }
  });

  useEffect(() => {
    context.setActiveView(activeView);
  }, [props.match.params.pagename]);

  /** **********************************************************
  /***  BUG. NEED TO FIND THE REASON FOR UNUSUAL STATE UPDATE ***
          No need to send oldData as argument.
          Should be available in the sate itself as `data`
 /*********************************************************** */

  const updateMyData = (newTaskData) => {
    const newTableData = cloneDeep(dataRef.current);
    const updatedIndex = newTableData.findIndex(
      (el) => el.taskId === newTaskData.taskId
    );
    newTableData[updatedIndex] = newTaskData;
    setData(newTableData);
  };

  const defaultColumnCells = {
    HTML: HTMLCell,
    Text: TextCell,
    Timer: TimerCell,
    Status: TagCell,
    Priority: NewInlinePriorityCell,
    Name: NameCell,
    DateTime: DateTimeCell,
    // TaskId: TaskIDCell,
    Source: SourceCell,
    Technician: TechnicianCell
  };
  // if we give table conditions first, operands will be array of aconditions. simple filters.
  // else if we give advanced, then it will be array of filter groups.

  return (
    <TableContainer>
      <SuperList
        noBulkSelect
        columns={columnGroup}
        data={data}
        setData={setData}
        defaultColumnCells={defaultColumnCells}
        isLoading={columnsLoading}
        fullHeight
        dynamicRowCount={paginationFlag}
        columnWidthPersonalization
        tableName={tableName}
        getAttributeName={getAttributeName}
        editCellId={editCellId}
        defaultSort={{
          attribute: 'createdTime',
          order: 'DESC'
        }}
        query={GET_TASK_LIST}
        client={TicketClient}
        appendViewIdToListInfo
        viewId={activeView.id}
        accessorKey="getTasks"
        dataKey="tasks"
        tableCondition={tableCondition}
        advanceFilterCondition={advanceFilterCondition}
        refreshEventEmitters={['refreshTaskList']}
      />
      {taskId && module && (
        <NewTaskModal
          taskId={taskId}
          module={module}
          actions={{
            updateTask: updateTask,
            deleteTask: deleteTask
          }}
        />
      )}
    </TableContainer>
  );
}

export default injectIntl(withRouter(TaskViewContainer));
